import { Entity } from '@api/api/entity-schema';
import { SnakeMeta } from '@api/api/timeseries-schema';
import { mapEntity } from '@api/mappers/mapEntity';

import {
  LatestValues,
  ReturnValue,
  WatchlistEntities,
} from './watchlist-types';

type MapEntityValuesProps = {
  entities: WatchlistEntities[];
  entity: Entity;
  latestValues: LatestValues;
  snakeMeta?: SnakeMeta;
  tag: string;
  liveReturn1D?: number;
};

export const mapEntityValues = ({
  entities,
  entity,
  snakeMeta,
  tag,
  latestValues,
  liveReturn1D,
}: MapEntityValuesProps): WatchlistEntities[] => {
  const formatReturn = (
    value?: {
      last_value: number;
      last_value_formatted: string;
      last_update: string;
    },
    includeLastUpdate = false
  ): ReturnValue => {
    return value
      ? {
          value: value.last_value,
          formattedValue: value.last_value_formatted,
          lastUpdate: includeLastUpdate ? value.last_update : undefined,
        }
      : { value: 0, formattedValue: '-' };
  };

  const getPrices = () => {
    if (!snakeMeta) {
      return undefined;
    }

    return {
      last_value: snakeMeta.last_value,
      before_last_value: snakeMeta.before_last_value,
    };
  };

  const mapReturns = () => ({
    rsi: formatReturn(latestValues['price_rsi(horiz=14d)']),
    liveReturn1D: {
      value: liveReturn1D,
      formattedValue:
        liveReturn1D !== undefined ? `${liveReturn1D}` : undefined,
    },
    return1D: formatReturn(latestValues['mom(horiz=1d)'], true),
    return1W: formatReturn(latestValues['mom(horiz=1w)']),
    return1M: formatReturn(latestValues['mom(horiz=1m)']),
    return3M: formatReturn(latestValues['mom(horiz=3m)']),
    return6M: formatReturn(latestValues['mom(horiz=6m)']),
    return1Y: formatReturn(latestValues['mom(horiz=12m)']),
    peTrailing: formatReturn(latestValues['pe_ibes_trailing_12m']),
    peForward: formatReturn(latestValues['pe_ibes_forward_12m']),
  });

  return entities.map(e => {
    const displayFormat = snakeMeta?.display_format
      ? snakeMeta.display_format
      : undefined;

    if (e.tag === tag) {
      return {
        ...e,
        asset: mapEntity(entity),
        returns: mapReturns(),
        prices: getPrices(),
        displayFormat: displayFormat,
        isLoading: false,
      };
    }

    return e;
  });
};
